import React, { useState, useRef, useEffect } from 'react';
import { QrCode, Camera, CheckCircle, AlertTriangle, X, RotateCcw, Flashlight } from 'lucide-react';

const QRCodeScanner = ({ onScan, onClose, isOpen }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [flashlightOn, setFlashlightOn] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('back');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isOpen && isScanning) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, isScanning]);

  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: cameraFacing,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // Simulate QR code detection (in real implementation, use a QR code library)
      setTimeout(() => {
        simulateQRDetection();
      }, 2000);
    } catch (err) {
      setError('Camera access denied or not available');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const simulateQRDetection = () => {
    // Simulate QR code detection with realistic medication data
    const mockQRData = {
      orderId: 'ORD-2024-001',
      residentId: 'RES001',
      residentName: 'Margaret Johnson',
      medication: 'Metformin 500mg',
      dose: '1 tablet',
      schedule: 'Twice daily with meals',
      batch: 'BATCH-2024-001',
      expiry: '2025-12-31',
      pharmacy: 'Sunshine Pharmacy',
      timestamp: new Date().toISOString()
    };

    setScanResult(mockQRData);
    setIsScanning(false);
  };

  const handleScan = () => {
    if (scanResult) {
      onScan(scanResult);
      setScanResult(null);
      onClose();
    }
  };

  const toggleFlashlight = () => {
    setFlashlightOn(!flashlightOn);
    // In real implementation, control camera flashlight
  };

  const switchCamera = () => {
    setCameraFacing(cameraFacing === 'back' ? 'front' : 'back');
    if (isScanning) {
      stopCamera();
      setTimeout(() => startCamera(), 100);
    }
  };

  const retryScan = () => {
    setScanResult(null);
    setError(null);
    setIsScanning(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <QrCode className="w-6 h-6 text-blue-600" />
            Scan Medication QR Code
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!isScanning && !scanResult && !error && (
          <div className="text-center py-8">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="w-12 h-12 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Ready to Scan</h3>
            <p className="text-gray-600 mb-6">Position the QR code within the camera view to scan medication information.</p>
            <button 
              onClick={() => setIsScanning(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Start Scanning
            </button>
          </div>
        )}

        {isScanning && !scanResult && (
          <div className="space-y-4">
            <div className="relative bg-gray-900 rounded-lg overflow-hidden">
              <video 
                ref={videoRef}
                className="w-full h-64 object-cover"
                playsInline
                muted
              />
              <div className="absolute inset-0 border-4 border-blue-500 border-dashed rounded-lg pointer-events-none">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-8 h-8 border-2 border-white rounded-full"></div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center gap-4">
              <button 
                onClick={toggleFlashlight}
                className={`p-3 rounded-lg ${flashlightOn ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'}`}
                title="Toggle Flashlight"
              >
                <Flashlight className="w-5 h-5" />
              </button>
              <button 
                onClick={switchCamera}
                className="p-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                title="Switch Camera"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">Position QR code within the frame</p>
            </div>
          </div>
        )}

        {scanResult && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-green-800">QR Code Scanned Successfully</h3>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Resident:</span>
                  <span className="font-medium">{scanResult.residentName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Medication:</span>
                  <span className="font-medium">{scanResult.medication}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dose:</span>
                  <span className="font-medium">{scanResult.dose}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Schedule:</span>
                  <span className="font-medium">{scanResult.schedule}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Batch:</span>
                  <span className="font-medium">{scanResult.batch}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expiry:</span>
                  <span className="font-medium">{scanResult.expiry}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleScan}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Confirm Administration
              </button>
              <button 
                onClick={retryScan}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Scan Again
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">Scan Error</h3>
              </div>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={retryScan}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button 
                onClick={onClose}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeScanner;
